import { useEffect } from 'react';

// Check if user has consented to analytics/marketing cookies
const hasAnalyticsConsent = () => {
  try {
    const consent = localStorage.getItem('cookie-consent');
    if (consent) {
      const preferences = JSON.parse(consent);
      return preferences.analytics || preferences.marketing;
    }
  } catch (e) {
    // If parsing fails, assume no consent
  }
  return false;
};

// Analytics hook for tracking events
export const useAnalytics = () => {
  
  // Track Google Ads Conversion for WhatsApp clicks
  // This sends the conversion event to Google Ads
  const trackWhatsAppConversion = (source = 'unknown') => {
    // Only track if gtag exists and user has consented
    if (typeof window !== 'undefined' && window.gtag && hasAnalyticsConsent()) {
      // Google Ads Conversion Event
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17608399769/gFaQCKO52I8dEJm3q8xB'
        // No value/currency as requested - we only measure clicks
      });
      console.log('Google Ads Conversion: WhatsApp click tracked -', source);
    }
  };

  // Track WhatsApp clicks (GA4 + Google Ads Conversion)
  const trackWhatsAppClick = (source = 'unknown') => {
    // Always try to track Google Ads conversion first
    trackWhatsAppConversion(source);
    
    // Also track in GA4 for analytics
    if (typeof window !== 'undefined' && window.gtag && hasAnalyticsConsent()) {
      window.gtag('event', 'whatsapp_click', {
        event_category: 'engagement',
        event_label: source,
        custom_parameter_1: 'whatsapp_click',
        send_to: 'G-1PCNWT67T9' // Real GA4 ID
      });
    }
    console.log('Analytics: WhatsApp click tracked -', source);
  };

  // Track form submissions
  const trackFormSubmission = (formName = 'contact_form') => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'form_submission', {
        event_category: 'engagement', 
        event_label: formName,
        send_to: 'G-1PCNWT67T9' // Real GA4 ID
      });
    }
    console.log('Analytics: Form submission tracked -', formName);
  };

  // Track price calculations
  const trackPriceCalculation = (sqm, price) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'price_calculation', {
        event_category: 'engagement',
        event_label: `${sqm}m²`,
        value: parseFloat(price),
        currency: 'EUR',
        send_to: 'G-1PCNWT67T9' // Real GA4 ID
      });
    }
    console.log('Analytics: Price calculation tracked -', sqm, 'm², €', price);
  };

  // Track calendar bookings
  const trackCalendarBooking = (date, sqm) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'calendar_booking', {
        event_category: 'conversion',
        event_label: date,
        custom_parameter_1: `${sqm}m²`,
        send_to: 'G-1PCNWT67T9' // Real GA4 ID
      });
    }
    console.log('Analytics: Calendar booking tracked -', date, sqm, 'm²');
  };

  // Track page views
  const trackPageView = (pageName, pageTitle) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', 'G-1PCNWT67T9', {
        page_title: pageTitle,
        page_location: window.location.href,
        page_path: window.location.pathname,
        custom_parameter_1: pageName
      });
    }
    console.log('Analytics: Page view tracked -', pageName);
  };

  // Track external link clicks
  const trackExternalClick = (linkName, destination) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'external_link_click', {
        event_category: 'engagement',
        event_label: linkName,
        custom_parameter_1: destination,
        send_to: 'G-1PCNWT67T9' // Real GA4 ID
      });
    }
    console.log('Analytics: External link tracked -', linkName, destination);
  };

  // Track scroll depth
  const trackScrollDepth = (percentage) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'scroll_depth', {
        event_category: 'engagement',
        event_label: `${percentage}%`,
        value: percentage,
        send_to: 'G-1PCNWT67T9' // Real GA4 ID
      });
    }
    console.log('Analytics: Scroll depth tracked -', percentage, '%');
  };

  return {
    trackWhatsAppClick,
    trackWhatsAppConversion,
    trackFormSubmission,
    trackPriceCalculation,
    trackCalendarBooking,
    trackPageView,
    trackExternalClick,
    trackScrollDepth
  };
};

// SEO and Performance utilities
export const useSEO = () => {
  // Update page meta tags dynamically
  const updateMetaTags = (title, description, keywords) => {
    if (typeof document !== 'undefined') {
      // Update title
      if (title) {
        document.title = title;
      }
      
      // Update description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription && description) {
        metaDescription.setAttribute('content', description);
      }
      
      // Update keywords
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords && keywords) {
        metaKeywords.setAttribute('content', keywords);
      }
      
      // Update Open Graph tags
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle && title) {
        ogTitle.setAttribute('content', title);
      }
      
      const ogDescription = document.querySelector('meta[property="og:description"]');
      if (ogDescription && description) {
        ogDescription.setAttribute('content', description);
      }
    }
  };

  // Lazy load images for performance
  const setupLazyLoading = () => {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            img.classList.remove('lazy');
            imageObserver.unobserve(img);
          }
        });
      });

      const lazyImages = document.querySelectorAll('img[data-src]');
      lazyImages.forEach(img => imageObserver.observe(img));
    }
  };

  return {
    updateMetaTags,
    setupLazyLoading
  };
};